import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { OrganizationData } from '../interfaces/Pod.interface';
import { fetchOrganizationBySlug } from '../services/PodService';

interface OrganizationContextType {
  organizationData: OrganizationData | null;
  loading: boolean;
  error: Error | null;
  refetchOrganization: (slug: string) => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ 
  children, 
}) => {
  const [organizationData, setOrganizationData] = useState<OrganizationData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const getOrganizationSlug = (): string => {
    // Get organization from hostname
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    const slug = parts[0] === 'localhost' ? 'urbannaps' : parts[0];
    //TODO: to be removed in future
    // const slug = 'urbannaps';
    return slug;
  };

  const fetchOrganization = async (slug: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchOrganizationBySlug(slug);
      setOrganizationData(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch organization data'));
      console.error('Failed to fetch organization data:', err);
    } finally {
      setLoading(false);
    }
  };

  const refetchOrganization = async (slug: string) => {
    await fetchOrganization(slug);
  };

  useEffect(() => {
    const slug = getOrganizationSlug();
    fetchOrganization(slug);
  }, []);

  const value = {
    organizationData,
    loading,
    error,
    refetchOrganization
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};
